// src/pages/TestPage.js (или где у тебя этот компонент)
import React, { useState } from 'react';
import { observer } from 'mobx-react-lite'; // <-- Импортируем observer
import testStore from '../stores/testStore'; // <-- Импортируем наш стор
import logger from '../utils/logger';
import apiClient from '../utils/apiClient'; // <-- Импортируем apiClient
import { 
    Button, 
    Typography, 
    CircularProgress, 
    Alert, 
    TextField, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper,
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Modal,
    Grid,
    Card,
    CardContent,
    CardMedia,
    IconButton,
    Chip,
    Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';

// Компонент для просмотра изображений
const ImageCarousel = ({ images }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    
    // Разделяем строку с изображениями на массив
    const imageArray = images ? images.split(',') : [];
    
    const handleNext = () => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % imageArray.length);
    };
    
    const handlePrev = () => {
        setCurrentIndex((prevIndex) => (prevIndex - 1 + imageArray.length) % imageArray.length);
    };
    
    if (imageArray.length === 0) return <Typography>Нет изображений</Typography>;
    
    return (
        <Box sx={{ position: 'relative', width: '100%', height: '400px' }}>
            <CardMedia
                component="img"
                image={imageArray[currentIndex]}
                alt={`Изображение ${currentIndex + 1}`}
                sx={{ 
                    width: '100%', 
                    height: '400px', 
                    objectFit: 'contain',
                    bgcolor: 'black'
                }}
            />
            
            {imageArray.length > 1 && (
                <>
                    <IconButton 
                        onClick={handlePrev}
                        sx={{
                            position: 'absolute',
                            left: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            bgcolor: 'rgba(255,255,255,0.7)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                        }}
                    >
                        <NavigateBeforeIcon />
                    </IconButton>
                    
                    <IconButton 
                        onClick={handleNext}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            bgcolor: 'rgba(255,255,255,0.7)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                        }}
                    >
                        <NavigateNextIcon />
                    </IconButton>
                    
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 8,
                            right: 8,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px'
                        }}
                    >
                        {currentIndex + 1} / {imageArray.length}
                    </Box>
                </>
            )}
        </Box>
    );
};

// Оборачиваем компонент в observer, чтобы он реагировал на изменения в testStore
function TestPage() {
    const [loading, setLoading] = useState(false);
    const [response, setResponse] = useState(null);
    const [error, setError] = useState(null);
    const [ads, setAds] = useState([]);
    const [selectedAd, setSelectedAd] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const handleParseRequest = async () => {
        try {
            setLoading(true);
            setError(null);
            setAds([]);
            
            // Используем apiClient вместо fetch для правильной авторизации
            const result = await apiClient.get('/api/parser/rest-app');
            
            const data = result.data; // Получаем данные напрямую из response.data
            setResponse(data);
            
            if (data.success && data.data && data.data.data) {
                setAds(data.data.data);
            }
            
            logger.log('Ответ от парсера получен:', data);
        } catch (err) {
            setError(err.message);
            logger.error('Ошибка при запросе к парсеру:', err);
        } finally {
            setLoading(false);
        }
    };
    
    const handleOpenModal = (ad) => {
        setSelectedAd(ad);
        setModalOpen(true);
    };
    
    const handleCloseModal = () => {
        setModalOpen(false);
    };

    // Получаем параметр по имени
    const getParamValue = (params, name) => {
        const param = params?.find(p => p.name === name);
        return param ? param.value : 'Не указано';
    };

    return (
        <div style={{ padding: '20px' }}>
            {import.meta.env.VITE_SHOW_COMPONENT_NAME === 'true' && <div onClick={() => logger.log(ads)}>% TestPage %</div>}
            <Typography variant="h4" gutterBottom>
                Тестирование парсера объявлений (REST-app)
            </Typography>
            
            <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" gutterBottom>
                    Тестовый запрос с параметрами:
                </Typography>
                <Typography variant="body2">
                    • Категория: 24 (Квартиры)
                </Typography>
                <Typography variant="body2">
                    • Лимит: 10 объявлений
                </Typography>
            </Box>
            
            <Button 
                variant="contained" 
                color="primary" 
                onClick={handleParseRequest}
                disabled={loading}
                style={{ marginBottom: '20px' }}
            >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Запустить парсер'}
            </Button>
            
            {error && (
                <Alert severity="error" style={{ marginBottom: '20px' }}>
                    Ошибка: {error}
                </Alert>
            )}
            
            {response && response.success && (
                <Alert severity="success" style={{ marginBottom: '20px' }}>
                    Статус: успешно получены данные 
                    {ads.length > 0 ? `, найдено объявлений: ${ads.length}` : ''}
                </Alert>
            )}
            
            {ads.length > 0 && (
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h5" gutterBottom>
                        Результаты поиска:
                    </Typography>
                    
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} aria-label="результаты парсинга">
                            <TableHead>
                                <TableRow>
                                {/*  <TableCell>ID</TableCell> */}
                                    <TableCell>Заголовок</TableCell>
                                    <TableCell>Цена</TableCell>
                                    <TableCell>Регион</TableCell>
                                    <TableCell>Город</TableCell>
                                    <TableCell>Детали</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {ads.map((ad) => (
                                    <TableRow key={ad.Id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        {/* <TableCell component="th" scope="row">
                                            {ad.Id}
                                        </TableCell> */}
                                        <TableCell>{ad.title}</TableCell>
                                        <TableCell>{new Intl.NumberFormat('ru-RU').format(ad.price)} ₽</TableCell>
                                        <TableCell>{ad.region}</TableCell>
                                        <TableCell>{ad.city}</TableCell>
                                        <TableCell>
                                            <Button 
                                                variant="outlined" 
                                                color="primary"
                                                onClick={() => handleOpenModal(ad)}
                                            >
                                                Посмотреть
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}
            
            {/* Модальное окно с детальной информацией об объявлении */}
            <Modal
                open={modalOpen}
                onClose={handleCloseModal}
                aria-labelledby="ad-details-modal"
                aria-describedby="detailed-information-about-ad"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '90%',
                    maxWidth: 1200,
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 0,
                    borderRadius: 1,
                    overflow: 'auto'
                }}>
                    {selectedAd && (
                        <>
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                p: 2, 
                                borderBottom: '1px solid #eee',
                                position: 'sticky',
                                top: 0,
                                bgcolor: 'background.paper',
                                zIndex: 1
                            }}>
                                <Typography id="ad-details-modal" variant="h6" component="h2">
                                    {selectedAd.title}
                                </Typography>
                                <IconButton 
                                    aria-label="Закрыть"
                                    onClick={handleCloseModal}
                                    sx={{ ml: 2 }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                            
                            <Grid container>
                                {/* Галерея изображений */}
                                <Grid item xs={12} md={6}>
                                    <ImageCarousel images={selectedAd.images} />
                                </Grid>
                                
                                {/* Информация о объявлении */}
                                <Grid item xs={12} md={6} sx={{ p: 2 }}>
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                        {new Intl.NumberFormat('ru-RU').format(selectedAd.price)} ₽
                                    </Typography>
                                    
                                    <Typography variant="body1" gutterBottom>
                                        {selectedAd.address}
                                    </Typography>
                                    
                                    <Box sx={{ my: 2 }}>
                                        <Grid container spacing={1}>
                                            <Grid item>
                                                <Chip label={`${getParamValue(selectedAd.params, 'Количество комнат')} комн.`} />
                                            </Grid>
                                            <Grid item>
                                                <Chip label={getParamValue(selectedAd.params, 'Общая площадь')} />
                                            </Grid>
                                            <Grid item>
                                                <Chip label={`${getParamValue(selectedAd.params, 'Этаж')}`} />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    
                                    <Divider sx={{ my: 2 }} />
                                    
                                    <Typography variant="subtitle1" gutterBottom>
                                        Контактная информация:
                                    </Typography>
                                    <Typography variant="body2" gutterBottom>
                                        Имя: {selectedAd.name}
                                    </Typography>
                                    <Typography variant="body2" gutterBottom>
                                        Телефон: {selectedAd.phone}
                                    </Typography>
                                    <Typography variant="body2" gutterBottom>
                                        Размещено: {new Date(selectedAd.time).toLocaleString('ru-RU')}
                                    </Typography>
                                    
                                    <Divider sx={{ my: 2 }} />
                                    
                                    <Typography variant="subtitle1" gutterBottom>
                                        Описание:
                                    </Typography>
                                    <Typography variant="body2" paragraph>
                                        {selectedAd.description}
                                    </Typography>
                                    
                                    <Divider sx={{ my: 2 }} />
                                    
                                    <Typography variant="subtitle1" gutterBottom>
                                        Характеристики:
                                    </Typography>
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        {selectedAd.params && selectedAd.params.map((param, idx) => (
                                            <Grid item xs={6} key={idx}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {param.name}:
                                                </Typography>
                                                <Typography variant="body2">
                                                    {param.value}
                                                </Typography>
                                            </Grid>
                                        ))}
                                    </Grid>
                                    
                                    <Button 
                                        variant="contained" 
                                        color="primary" 
                                        fullWidth
                                        href={selectedAd.url}
                                        target="_blank"
                                        sx={{ mt: 2 }}
                                    >
                                        Открыть оригинал объявления
                                    </Button>
                                </Grid>
                            </Grid>
                        </>
                    )}
                </Box>
            </Modal>
        </div>
    );
}

export default observer(TestPage); // <-- Не забываем observer
